import React from "react";
import type { TFunction } from "i18next";
import { Autocomplete, TextField } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import type { MapMarkerLocation } from "../../../features/Reports/typings/reports.types";
import {
  facilityReportMapLocationAutocompleteRootSx,
  facilityReportMapLocationPaperSx,
  facilityReportMapLocationTextFieldSx
} from "./facility-report-map-location-autocomplete.styles";

export type LocationOption = {
  locationInventoryId: number;
};

export function isSameLocationOption(option: LocationOption, value: LocationOption): boolean {
  return option.locationInventoryId === value.locationInventoryId;
}

export type FacilityReportMapLocationAutocompleteProps = {
  mapFullscreenPortalRoot: Element | null;
  isMobile: boolean;
  muiTheme: Theme;
  t: TFunction;
  selectedLocationOption: MapMarkerLocation | null;
  locationOptions: MapMarkerLocation[];
  isLocationAutocompleteEnabled: boolean;
  onClearSelection: () => void;
  onPickLocation: (location: MapMarkerLocation) => void;
  locationAutocompleteTopCss: string;
  locationAutocompleteLeftPx: number;
};

export const FacilityReportMapLocationAutocomplete: React.FC<
  FacilityReportMapLocationAutocompleteProps
> = ({
  mapFullscreenPortalRoot,
  isMobile,
  muiTheme,
  t,
  selectedLocationOption,
  locationOptions,
  isLocationAutocompleteEnabled,
  onClearSelection,
  onPickLocation,
  locationAutocompleteTopCss,
  locationAutocompleteLeftPx
}) => {
  const isMapFullscreen = mapFullscreenPortalRoot != null;

  const textFieldSx = facilityReportMapLocationTextFieldSx(muiTheme);
  const paperSlotSx = facilityReportMapLocationPaperSx(muiTheme);
  const rootSx = facilityReportMapLocationAutocompleteRootSx({
    theme: muiTheme,
    isMobile,
    isMapFullscreen,
    topCss: locationAutocompleteTopCss,
    leftPx: locationAutocompleteLeftPx
  });

  const slotProps = {
    paper: { sx: paperSlotSx },
    ...(isMapFullscreen ? { popper: { container: mapFullscreenPortalRoot } } : {})
  };

  return (
    <Autocomplete
      data-testid="facility-report-location-autocomplete-google-map"
      value={selectedLocationOption}
      options={locationOptions}
      getOptionLabel={option => option.name}
      isOptionEqualToValue={isSameLocationOption}
      disabled={!isLocationAutocompleteEnabled}
      onChange={(_, option) => {
        if (!option) {
          onClearSelection();
          return;
        }

        onPickLocation(option);
      }}
      renderInput={params => (
        <TextField
          {...params}
          {...(isMobile ? { fullWidth: true } : {})}
          label={t("Location")}
          variant="outlined"
          placeholder={selectedLocationOption ? "" : t("Select Location")}
          sx={textFieldSx}
        />
      )}
      slotProps={slotProps}
      sx={rootSx}
    />
  );
};
